import { FormEvent, useState } from 'react'
import { useAdminProgram, useUpdateProgram } from 'src/hooks/useAdminAffiliates'
import { Program } from 'src/types/Affiliate'
import { notify } from 'src/utils/notify'
import { errorMessage } from '../affiliate/labels'
import {
    Banner,
    Card,
    CardTitle,
    Checkbox,
    ErrorText,
    Grid,
    InlineSpinner,
    Input,
    Label,
    Muted,
    PrimaryButton,
    Row,
} from '../affiliate/ui'
import { FormError, formMessage, formatValue, readDollars, readInt, readPercent } from './shared'

type ProgramPatch = Partial<Omit<Program, 'history'>>

const toForm = (program: Program) => ({
    firstMonth: String(program.firstMonthBps / 100),
    recurring: String(program.recurringBps / 100),
    holdDays: String(program.holdDays),
    minPayout: String(program.minPayoutCents / 100),
    applicationsOpen: program.applicationsOpen,
    requirePayoutApproval: program.requirePayoutApproval,
    termsVersion: program.termsVersion,
    termsUrl: program.termsUrl,
    landingPath: program.landingPath,
    reapplyCooldownDays: String(program.reapplyCooldownDays),
    codeTrialDays: String(program.codeTrialDays),
    maxCampaignCodes: String(program.maxCampaignCodes),
})

type ProgramForm = ReturnType<typeof toForm>

const required = <T,>(value: T | undefined, label: string) => {
    if (value === undefined) throw new FormError(`${label} is required.`)
    return value
}

const buildPatch = (program: Program, form: ProgramForm) => {
    const termsVersion = form.termsVersion.trim()
    const termsUrl = form.termsUrl.trim()
    const landingPath = form.landingPath.trim()
    if (!termsVersion) throw new FormError('Terms version is required.')
    if (!/^https?:\/\//i.test(termsUrl)) throw new FormError('Terms URL must be a full http(s) URL.')
    if (!/^\/(?!\/)/.test(landingPath)) throw new FormError('Landing path must start with a single "/".')

    const next: Omit<Program, 'history'> = {
        firstMonthBps: required(readPercent(form.firstMonth, 'First-month rate'), 'First-month rate'),
        recurringBps: required(readPercent(form.recurring, 'Recurring rate'), 'Recurring rate'),
        holdDays: readInt(form.holdDays, 'Hold days', 0, 180),
        minPayoutCents: required(readDollars(form.minPayout, 'Payout threshold'), 'Payout threshold'),
        applicationsOpen: form.applicationsOpen,
        requirePayoutApproval: form.requirePayoutApproval,
        termsVersion,
        termsUrl,
        landingPath,
        reapplyCooldownDays: readInt(form.reapplyCooldownDays, 'Re-apply cooldown', 0, 365),
        codeTrialDays: readInt(form.codeTrialDays, 'Code trial length', 1, 30),
        maxCampaignCodes: readInt(form.maxCampaignCodes, 'Campaign codes per affiliate', 0, 50),
    }

    const patch: ProgramPatch = {}
    ;(Object.keys(next) as (keyof typeof next)[]).forEach((field) => {
        if (next[field] !== program[field]) (patch as Record<string, unknown>)[field] = next[field]
    })
    return patch
}

const ProgramEditor = ({ program }: { program: Program }) => {
    const update = useUpdateProgram()
    const [form, setForm] = useState(() => toForm(program))
    const set = <K extends keyof ProgramForm>(field: K, value: ProgramForm[K]) =>
        setForm((current) => ({ ...current, [field]: value }))

    const submit = (e: FormEvent) => {
        e.preventDefault()
        let patch: ProgramPatch
        try {
            patch = buildPatch(program, form)
        } catch (err) {
            notify(formMessage(err) || 'Check the form.', 'error')
            return
        }
        if (Object.keys(patch).length === 0) {
            notify('Nothing changed.', 'info')
            return
        }
        if (
            patch.termsVersion !== undefined &&
            !window.confirm(
                `Change the terms version to "${patch.termsVersion}"? Every affiliate will have to accept the terms again before they can request a payout.`
            )
        ) {
            return
        }
        update.mutate(patch, {
            onSuccess: () => notify('Program settings saved.', 'success'),
            onError: (err) => notify(errorMessage(err), 'error'),
        })
    }

    const termsChanged = form.termsVersion.trim() !== program.termsVersion

    return (
        <Card as="form" onSubmit={submit}>
            <CardTitle>Program settings</CardTitle>
            <Muted>Rate and threshold changes apply to affiliates without an override, for invoices paid from now on.</Muted>
            <Grid $min={200}>
                <Label>
                    First month %
                    <Input inputMode="decimal" value={form.firstMonth} onChange={(e) => set('firstMonth', e.target.value)} />
                </Label>
                <Label>
                    Recurring %
                    <Input inputMode="decimal" value={form.recurring} onChange={(e) => set('recurring', e.target.value)} />
                </Label>
                <Label>
                    Hold days
                    <Input inputMode="numeric" value={form.holdDays} onChange={(e) => set('holdDays', e.target.value)} />
                </Label>
                <Label>
                    Payout threshold $
                    <Input inputMode="decimal" value={form.minPayout} onChange={(e) => set('minPayout', e.target.value)} />
                </Label>
                <Label>
                    Re-apply cooldown (days)
                    <Input
                        inputMode="numeric"
                        value={form.reapplyCooldownDays}
                        onChange={(e) => set('reapplyCooldownDays', e.target.value)}
                    />
                </Label>
                <Label>
                    Free trial through a code (days)
                    <Input
                        inputMode="numeric"
                        value={form.codeTrialDays}
                        onChange={(e) => set('codeTrialDays', e.target.value)}
                    />
                </Label>
                <Label>
                    Campaign codes per affiliate
                    <Input
                        inputMode="numeric"
                        value={form.maxCampaignCodes}
                        onChange={(e) => set('maxCampaignCodes', e.target.value)}
                    />
                </Label>
                <Label>
                    Landing path
                    <Input value={form.landingPath} onChange={(e) => set('landingPath', e.target.value)} placeholder="/" />
                </Label>
                <Label>
                    Terms version
                    <Input value={form.termsVersion} onChange={(e) => set('termsVersion', e.target.value)} maxLength={40} />
                </Label>
                <Label>
                    Terms URL
                    <Input type="url" value={form.termsUrl} onChange={(e) => set('termsUrl', e.target.value)} />
                </Label>
            </Grid>
            <Checkbox>
                <input
                    type="checkbox"
                    checked={form.applicationsOpen}
                    onChange={(e) => set('applicationsOpen', e.target.checked)}
                />
                Applications open
            </Checkbox>
            <Checkbox>
                <input
                    type="checkbox"
                    checked={form.requirePayoutApproval}
                    onChange={(e) => set('requirePayoutApproval', e.target.checked)}
                />
                Require admin approval for payouts
            </Checkbox>
            {termsChanged && (
                <Banner $tone="warn">
                    <p>Changing the terms version makes every affiliate accept the terms again.</p>
                </Banner>
            )}
            <Row>
                <PrimaryButton type="submit" disabled={update.isPending}>
                    {update.isPending ? 'Saving…' : 'Save settings'}
                </PrimaryButton>
            </Row>
        </Card>
    )
}

const ProgramHistory = ({ history }: { history: Program['history'] }) => (
    <Card>
        <CardTitle>Change history</CardTitle>
        {history.length === 0 ? (
            <Muted>No changes yet.</Muted>
        ) : (
            <ul style={{ display: 'flex', flexDirection: 'column', gap: 12, listStyle: 'none' }}>
                {history
                    .slice()
                    .reverse()
                    .map((entry, index) => (
                        <li key={index}>
                            <Muted>
                                {new Date(entry.changedAt).toLocaleString()}
                                {entry.changedBy && `, by ${entry.changedBy}`}
                            </Muted>
                            {Object.keys(entry.changes).map((field) => (
                                <p key={field} style={{ overflowWrap: 'anywhere' }}>
                                    <strong>{field}</strong>: {formatValue(entry.changes[field].from, field)} →{' '}
                                    {formatValue(entry.changes[field].to, field)}
                                </p>
                            ))}
                        </li>
                    ))}
            </ul>
        )}
    </Card>
)

const ProgramTab = () => {
    const { data, isLoading, error } = useAdminProgram()
    if (isLoading) return <InlineSpinner />
    if (error) return <ErrorText>{errorMessage(error)}</ErrorText>
    if (!data) return null
    return (
        <>
            <ProgramEditor program={data} />
            <ProgramHistory history={data.history || []} />
        </>
    )
}

export default ProgramTab
